import { useState, useEffect } from 'react';
import { motion, AnimatePresence, useMotionValue, useTransform, animate } from 'framer-motion';
import { X, Star, Play, CheckCircle, ArrowRight, Trophy, Users, TrendingUp, Sparkles, Quote, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Intervention {
  id: string;
  title: string;
  message: string | null;
  category: string;
  video_url: string | null;
  image_url: string | null;
  content: Record<string, any>;
  xp_reward: number;
  delivery_method: string;
}

interface InterventionPopupProps {
  workerId: string;
  onNavigateToQuest?: (questId: string) => void;
}

// Animated counter component
function AnimatedCounter({ value, suffix = '%' }: { value: number; suffix?: string }) {
  const count = useMotionValue(0);
  const rounded = useTransform(count, (v) => Math.round(v));
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    const controls = animate(count, value, { duration: 1.5, ease: 'easeOut' });
    const unsubscribe = rounded.on('change', (v) => setDisplayValue(v));
    return () => {
      controls.stop();
      unsubscribe();
    };
  }, [value, count, rounded]);

  return (
    <span className="tabular-nums">
      {displayValue}{suffix}
    </span>
  );
}

// Confetti particle component
function ConfettiParticle({ delay, color }: { delay: number; color: string }) {
  return (
    <motion.div
      className="absolute w-2 h-2 rounded-full"
      style={{ backgroundColor: color }}
      initial={{ 
        x: 0, 
        y: 0, 
        scale: 0,
        rotate: 0 
      }}
      animate={{ 
        x: (Math.random() - 0.5) * 150,
        y: (Math.random() - 0.5) * 150,
        scale: [0, 1, 0],
        rotate: Math.random() * 360
      }}
      transition={{ 
        duration: 1.2, 
        delay,
        ease: 'easeOut'
      }}
    />
  );
}

export default function InterventionPopup({ workerId, onNavigateToQuest }: InterventionPopupProps) {
  const [intervention, setIntervention] = useState<Intervention | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (workerId) {
      loadPendingIntervention();
    }
  }, [workerId]);

  const loadPendingIntervention = async () => {
    try {
      const { data, error } = await supabase
        .from('interventions')
        .select('*')
        .eq('target_worker_id', workerId)
        .eq('delivery_method', 'popup')
        .eq('status', 'pending')
        .order('created_at', { ascending: true })
        .limit(1)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading intervention:', error);
        return;
      }

      if (data) {
        setIntervention(data as Intervention);
        await supabase
          .from('interventions')
          .update({ 
            status: 'delivered', 
            delivered_at: new Date().toISOString() 
          })
          .eq('id', data.id);
        
        setTimeout(() => {
          setIsVisible(true);
          if (data.category === 'recognition' || data.category === 'gamification') {
            setShowConfetti(true);
          }
        }, 500);
      }
    } catch (error) {
      console.error('Error loading intervention:', error);
    }
  };

  const handleDismiss = async () => {
    if (!intervention) return;

    try {
      await supabase
        .from('interventions')
        .update({ 
          status: 'dismissed',
          dismissed_at: new Date().toISOString(),
          viewed_at: new Date().toISOString()
        })
        .eq('id', intervention.id);

      setIsVisible(false);
      setTimeout(() => {
        setIntervention(null);
        setShowConfetti(false);
        loadPendingIntervention();
      }, 300);
    } catch (error) {
      console.error('Error dismissing intervention:', error);
    }
  };

  const handleComplete = async () => {
    if (!intervention) return;

    try {
      await supabase
        .from('interventions')
        .update({ 
          status: 'completed',
          completed_at: new Date().toISOString(),
          viewed_at: new Date().toISOString()
        })
        .eq('id', intervention.id);

      if (intervention.xp_reward > 0) {
        await supabase.from('xp_transactions').insert({
          worker_id: workerId,
          xp_amount: intervention.xp_reward,
          reason: `Completed intervention: ${intervention.category}`,
        });

        const { data: profile } = await supabase
          .from('worker_profiles')
          .select('total_xp')
          .eq('id', workerId)
          .single();
        
        if (profile) {
          await supabase
            .from('worker_profiles')
            .update({ total_xp: (profile.total_xp || 0) + intervention.xp_reward })
            .eq('id', workerId);
        }

        toast.success(`+${intervention.xp_reward} XP!`);
      }

      setIsVisible(false);
      setTimeout(() => {
        setIntervention(null);
        setShowConfetti(false);
        loadPendingIntervention();
      }, 300);
    } catch (error) {
      console.error('Error completing intervention:', error);
    }
  };

  const handleViewQuest = () => {
    if (intervention?.content?.quest_id && onNavigateToQuest) {
      onNavigateToQuest(intervention.content.quest_id);
      handleDismiss();
    }
  };

  const getYouTubeEmbedUrl = (url: string) => {
    const videoId = url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/)?.[1];
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : url;
  };

  const getVideoUrl = () => {
    if (intervention?.video_url) return intervention.video_url;
    if (intervention?.content?.videoMessage?.videoUrl) return intervention.content.videoMessage.videoUrl;
    return null;
  };

  const getImageUrl = () => {
    if (intervention?.image_url) return intervention.image_url;
    if (intervention?.content?.videoMessage?.thumbnailUrl) return intervention.content.videoMessage.thumbnailUrl;
    if (intervention?.content?.successStory?.photoUrl) return intervention.content.successStory.photoUrl;
    return null;
  };

  const extractPercentage = (text: string): number | null => {
    const match = text.match(/(\d+)%/);
    return match ? parseInt(match[1]) : null;
  };

  // Get category icon
  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'norm_nudge': return '📊';
      case 'recognition': 
      case 'gamification': return '🏆';
      case 'success_story': 
      case 'social_proof': return '⭐';
      case 'video_message': return '🎬';
      case 'consequence_framing': return '⚠️';
      default: return '💡';
    }
  };

  // Render category-specific content
  const renderCategoryContent = () => {
    if (!intervention) return null;
    const { category, content, message } = intervention;

    // Norm Nudge - Social proof with animated counter
    if (category === 'norm_nudge') {
      const statText = content?.normNudge?.statistic || content?.normNudge?.manualValue || message || '';
      const percentage = extractPercentage(statText);
      
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="relative overflow-hidden rounded-xl p-5 bg-muted/30 border border-border"
        >
          <div className="relative z-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-muted-foreground" />
              </div>
              <Badge variant="secondary" className="text-xs">
                Fakta Rekan Kerja
              </Badge>
            </div>
            
            {percentage !== null ? (
              <div className="space-y-3">
                <div className="text-4xl font-bold text-foreground">
                  <AnimatedCounter value={percentage} />
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  {statText.replace(/\d+%/, '').trim() || 'rekan kerjamu sudah melakukannya'}
                </p>
                {/* Progress bar visualization */}
                <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                  <motion.div 
                    className="h-full bg-primary rounded-full"
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 1.5, ease: 'easeOut' }}
                  />
                </div>
              </div>
            ) : (
              <p className="text-base font-medium text-foreground">{statText}</p>
            )}
          </div>
        </motion.div>
      );
    }

    // Recognition - Celebration with confetti
    if (category === 'recognition' || category === 'gamification') {
      const { achievementType, reason, badgeIcon } = content?.recognition || {};
      
      return (
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="relative text-center py-6"
        >
          {/* Confetti */}
          {showConfetti && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
              {['hsl(var(--primary))', 'hsl(var(--accent))', 'hsl(var(--secondary))'].map((color, i) => (
                Array.from({ length: 4 }).map((_, j) => (
                  <ConfettiParticle key={`${i}-${j}`} delay={j * 0.1} color={color} />
                ))
              ))}
            </div>
          )}
          
          {/* Badge */}
          <motion.div 
            className="relative inline-block mb-4"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: 'spring', bounce: 0.5, delay: 0.3 }}
          >
            <div className="relative w-20 h-20 rounded-full bg-muted flex items-center justify-center border-2 border-border">
              <span className="text-4xl">{badgeIcon || '🏆'}</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <Badge variant="secondary" className="mb-3">
              {achievementType === 'badge' && '🎖️ Badge Baru'}
              {achievementType === 'milestone' && '🎯 Milestone Tercapai'}
              {achievementType === 'streak' && '🔥 Streak Achievement'}
              {!achievementType && '⭐ Pencapaian'}
            </Badge>
            <p className="text-muted-foreground leading-relaxed max-w-xs mx-auto">
              {reason || message}
            </p>
          </motion.div>
        </motion.div>
      );
    }

    // Success Story - Testimonial card style
    if (category === 'success_story' || category === 'social_proof') {
      const { isAnonymous, peerName, storyDescription, photoUrl } = content?.successStory || {};
      const displayName = isAnonymous ? 'Rekan Kerja' : (peerName || 'Rekan Kerja');
      const imageUrl = photoUrl || getImageUrl();
      
      return (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="space-y-4"
        >
          {/* Quote card */}
          <div className="relative bg-muted/30 rounded-xl p-5 border border-border">
            {/* Large quote mark */}
            <Quote className="absolute top-3 left-3 h-6 w-6 text-muted-foreground/30" />
            
            {/* Profile section */}
            <div className="flex items-center gap-3 mb-4 relative z-10">
              <div className="relative">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center border border-border">
                  {imageUrl ? (
                    <img src={imageUrl} alt={displayName} className="w-full h-full rounded-full object-cover" />
                  ) : (
                    <span className="text-xl font-bold text-muted-foreground">
                      {displayName.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
              </div>
              <div>
                <p className="font-medium text-foreground">{displayName}</p>
                <p className="text-xs text-muted-foreground">Safety Champion</p>
              </div>
            </div>
            
            {/* Story text */}
            <p className="text-muted-foreground leading-relaxed italic pl-6">
              "{storyDescription || message}"
            </p>
          </div>
        </motion.div>
      );
    }

    // Video Message
    if (category === 'video_message') {
      const videoUrl = getVideoUrl();
      const thumbnailUrl = getImageUrl();
      const callToAction = content?.videoMessage?.callToAction || message;
      
      if (videoUrl) {
        return (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-3"
          >
            <div className="relative aspect-video rounded-xl overflow-hidden bg-muted border border-border">
              {isVideoPlaying ? (
                videoUrl.includes('youtube') || videoUrl.includes('youtu.be') ? (
                  <iframe
                    src={getYouTubeEmbedUrl(videoUrl)}
                    className="absolute inset-0 w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={videoUrl}
                    className="absolute inset-0 w-full h-full object-contain"
                    controls
                    autoPlay
                  />
                )
              ) : (
                <motion.div 
                  className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                  onClick={() => setIsVideoPlaying(true)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {thumbnailUrl && (
                    <img 
                      src={thumbnailUrl} 
                      alt="Video thumbnail"
                      className="absolute inset-0 w-full h-full object-cover"
                    />
                  )}
                  <div className="absolute inset-0 bg-background/40 group-hover:bg-background/50 transition-colors" />
                  <motion.div 
                    className="relative z-10 w-14 h-14 rounded-full bg-background flex items-center justify-center shadow-lg border border-border"
                    whileHover={{ scale: 1.1 }}
                  >
                    <Play className="h-6 w-6 text-primary fill-primary ml-1" />
                  </motion.div>
                </motion.div>
              )}
            </div>
            {callToAction && (
              <p className="text-sm text-muted-foreground text-center">
                {callToAction}
              </p>
            )}
          </motion.div>
        );
      }
    }

    return null;
  };

  if (!intervention || !isVisible) return null;

  const videoUrl = getVideoUrl();
  const imageUrl = getImageUrl();
  const hasCategorySpecificContent = ['norm_nudge', 'recognition', 'gamification', 'success_story', 'social_proof', 'video_message'].includes(intervention.category);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm"
        onClick={handleDismiss}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md"
        >
          <Card className="overflow-hidden border bg-card shadow-lg">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <div className="flex items-center gap-2">
                <span className="text-xl">{getCategoryIcon(intervention.category)}</span>
                <Badge variant="secondary" className="text-xs capitalize">
                  {intervention.category.replace('_', ' ')}
                </Badge>
              </div>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                onClick={handleDismiss}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            {/* Content */}
            <div className="p-5 space-y-4">
              <motion.h2 
                className="text-lg font-semibold text-foreground"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                {intervention.title}
              </motion.h2>
              
              {/* Category-specific content */}
              {hasCategorySpecificContent ? (
                renderCategoryContent()
              ) : (
                <>
                  {intervention.message && (
                    <p className="text-muted-foreground leading-relaxed">
                      {intervention.message}
                    </p>
                  )}

                  {videoUrl && (
                    <div className="relative aspect-video rounded-xl overflow-hidden bg-muted border border-border">
                      {isVideoPlaying ? (
                        <iframe
                          src={getYouTubeEmbedUrl(videoUrl)}
                          className="absolute inset-0 w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        />
                      ) : (
                        <div 
                          className="absolute inset-0 flex items-center justify-center cursor-pointer group"
                          onClick={() => setIsVideoPlaying(true)}
                        >
                          {imageUrl && (
                            <img 
                              src={imageUrl} 
                              alt="Video thumbnail"
                              className="absolute inset-0 w-full h-full object-cover"
                            />
                          )}
                          <div className="absolute inset-0 bg-background/40 group-hover:bg-background/50 transition-colors" />
                          <div className="relative z-10 w-14 h-14 rounded-full bg-background flex items-center justify-center shadow-lg border border-border group-hover:scale-110 transition-transform">
                            <Play className="h-6 w-6 text-primary fill-primary ml-1" />
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {imageUrl && !videoUrl && (
                    <div className="rounded-xl overflow-hidden border border-border">
                      <img 
                        src={imageUrl} 
                        alt={intervention.title}
                        className="w-full h-auto"
                      />
                    </div>
                  )}
                </>
              )}

              {/* Learning points */}
              {intervention.content?.learning_points && (
                <div className="space-y-2">
                  {intervention.content.learning_points.map((point: string, index: number) => (
                    <motion.div 
                      key={index} 
                      className="flex items-start gap-2"
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.1 }}
                    >
                      <CheckCircle className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                      <span className="text-sm text-muted-foreground">{point}</span>
                    </motion.div>
                  ))}
                </div>
              )}

              {/* XP Reward */}
              {intervention.xp_reward > 0 && (
                <motion.div 
                  className="flex items-center justify-center gap-2 py-2 px-4 rounded-full bg-muted border border-border"
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4 }}
                >
                  <Star className="h-4 w-4 text-primary" />
                  <span className="font-medium text-foreground">+{intervention.xp_reward} XP</span>
                </motion.div>
              )}
            </div>

            {/* Actions */}
            <div className="p-4 pt-0 flex gap-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={handleDismiss}
              >
                Nanti
              </Button>
              {intervention.content?.quest_id ? (
                <Button 
                  className="flex-1"
                  onClick={handleViewQuest}
                >
                  Lihat Quest
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  className="flex-1"
                  onClick={handleComplete}
                >
                  Mengerti
                  <CheckCircle className="h-4 w-4 ml-2" />
                </Button>
              )}
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}